import React, { useEffect, useState } from "react";
import { api, API, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import StreakCard from "@/components/StreakCard";
import { Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Circle, Sparkles, Lock, PlayCircle, X } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [chapters, setChapters] = useState([]);
  const [progress, setProgress] = useState({ chapters: [], exam: null });
  const [welcome, setWelcome] = useState(null); // {video_url, instructor_name, instructor_photo}
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/course"), api.get("/progress")]).then(([c, p]) => {
      setChapters(c.data);
      setProgress(p.data);
    });
    // Only students see the welcome video, once per browser
    if (user?.role === "student") {
      const seenKey = `dt_welcome_seen_${user.id}`;
      if (!localStorage.getItem(seenKey)) {
        api.get("/welcome-video").then(r => {
          if (r.data?.video_url) {
            setWelcome(r.data);
            setShowWelcome(true);
          }
        }).catch(() => {});
      }
    }
  }, [user?.id, user?.role]);

  const dismissWelcome = () => {
    if (user?.id) localStorage.setItem(`dt_welcome_seen_${user.id}`, "1");
    setShowWelcome(false);
  };

  const completedChapters = progress.chapters.filter(c => c.passed).length;
  const totalChapters = chapters.length || 13;
  const pct = Math.round((completedChapters / totalChapters) * 100);
  const isCompletedMap = new Map(progress.chapters.map(c => [c.chapter_id, c]));
  const isUnpaid = user?.role === "student" && user?.access_status !== "active";

  return (
    <Layout>
      {showWelcome && welcome?.video_url && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 grid place-items-center p-4" data-testid="welcome-video-modal">
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full overflow-hidden shadow-2xl">
            <div className="p-5 flex items-center justify-between border-b border-border">
              <div className="flex items-center gap-3 min-w-0">
                {welcome.instructor_photo && (
                  <img src={`${BACKEND_URL}${welcome.instructor_photo}`} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="label-caps text-[10px] text-accent flex items-center gap-1"><PlayCircle size={11} /> A quick welcome from your instructor</div>
                  <div className="font-serif text-xl truncate">{welcome.instructor_name || "Your instructor"}</div>
                </div>
              </div>
              <button
                onClick={dismissWelcome}
                data-testid="welcome-close-btn"
                className="p-2 rounded-full hover:bg-secondary shrink-0"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <video
              src={`${BACKEND_URL}${welcome.video_url}`}
              controls
              autoPlay
              className="w-full aspect-video bg-black"
              data-testid="welcome-video-player"
              onEnded={dismissWelcome}
            />
            <div className="p-4 flex justify-end">
              <Button onClick={dismissWelcome} data-testid="welcome-continue-btn">Let's get started <ArrowRight size={14} className="ml-2" /></Button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto p-6 lg:p-12 space-y-10">
        {isUnpaid && (
          <div className="border border-accent rounded-md p-5 bg-accent/10 flex flex-wrap items-center justify-between gap-4" data-testid="enroll-banner">
            <div className="flex items-start gap-3">
              <Sparkles size={20} className="text-accent shrink-0 mt-1" />
              <div>
                <div className="label-caps text-accent">Enroll to unlock everything</div>
                <div className="font-serif text-2xl mt-1">You're on a preview. Enroll to earn your certificate.</div>
                <div className="text-sm text-muted-foreground mt-1">Full access includes the final exam, downloadable certificate, and unlimited AI Teacher help.</div>
              </div>
            </div>
            <Link to="/pricing" className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-accent text-accent-foreground text-sm hover:opacity-90" data-testid="enroll-cta">
              See plans <ArrowRight size={14} />
            </Link>
          </div>
        )}
        <header className="grid lg:grid-cols-3 gap-6 items-end">
          <div className="lg:col-span-2">
            <div className="label-caps mb-3">Your dashboard</div>
            <h1 className="font-serif text-4xl lg:text-6xl leading-none">Good day, {user?.name?.split(" ")[0]}.</h1>
            <p className="mt-4 text-muted-foreground max-w-lg">Continue the White Label Tax Course. Complete every chapter and pass the final exam with 80% to earn your certificate.</p>
          </div>
          <div className="bg-card border border-border rounded-md p-6">
            <div className="label-caps mb-2">Progress</div>
            <div className="font-serif text-5xl leading-none" data-testid="progress-percent">{pct}%</div>
            <Progress value={pct} className="mt-3" />
            <div className="mt-2 text-sm text-muted-foreground">{completedChapters} of {totalChapters} chapters passed</div>
          </div>
        </header>

        <StreakCard />

        {user?.has_afsp_access && (
          <div className="border border-accent/40 bg-accent/5 rounded-md p-6 flex items-center gap-4" data-testid="afsp-dashboard-card">
            <div className="h-12 w-12 rounded-md bg-accent/15 text-accent grid place-items-center shrink-0">
              <Sparkles size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-caps text-accent mb-0.5">AFSP Prep Kit</div>
              <h3 className="font-serif text-xl leading-tight">Your AFTR practice exam is waiting.</h3>
              <p className="text-sm text-muted-foreground">Download the Circular 230 study guide and take the 18-question exam — unlimited retakes.</p>
            </div>
            <Button asChild data-testid="afsp-dashboard-open">
              <Link to="/afsp-prep">Open <ArrowRight size={14} className="ml-2" /></Link>
            </Button>
          </div>
        )}

        <section>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-serif text-3xl">Curriculum</h2>
            <Link to="/course" className="text-sm underline" data-testid="view-full-course">View all</Link>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {chapters.map((ch) => {
              const p = isCompletedMap.get(ch.id);
              const passed = p?.passed;
              const locked = ch.locked;
              return (
                <Link
                  key={ch.id}
                  to={`/course/${ch.id}`}
                  data-testid={`chapter-card-${ch.id}`}
                  className={`group border border-border rounded-md p-6 bg-card transition-colors ${locked ? "opacity-75 hover:border-accent" : "hover:border-primary"}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="label-caps">Ch. {ch.id} · {ch.category}</span>
                    {locked ? <Lock size={16} className="text-accent" data-testid={`dashboard-lock-${ch.id}`} /> :
                      passed ? <CheckCircle2 size={16} className="text-primary" /> : <Circle size={16} className="text-muted-foreground" />}
                  </div>
                  <h3 className="font-serif text-2xl leading-tight mb-2">{ch.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{ch.summary}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{ch.reading_time} min read</span>
                    <span className={`flex items-center gap-1 ${locked ? "text-accent" : "text-foreground group-hover:text-accent"}`}>
                      {locked ? "Enroll to unlock" : p ? `Score: ${p.score}%` : "Start"} <ArrowRight size={12} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="grid md:grid-cols-2 gap-4">
          <Link to="/practice" data-testid="cta-practice" className="border border-border rounded-md p-6 bg-secondary hover:bg-primary hover:text-primary-foreground transition-colors group">
            <div className="label-caps mb-2">Live practice</div>
            <h3 className="font-serif text-3xl leading-tight">1040 Case Studies</h3>
            <p className="text-sm mt-2 opacity-80">Work through 4 real-world returns with instant feedback.</p>
          </Link>
          <Link to="/exam" data-testid="cta-exam" className="border border-border rounded-md p-6 bg-accent text-accent-foreground hover:opacity-90 transition-opacity">
            <div className="label-caps mb-2 text-accent-foreground/80">Final</div>
            <h3 className="font-serif text-3xl leading-tight">Take the Final Exam</h3>
            <p className="text-sm mt-2 opacity-90">25 questions. 80% to pass. Unlocks your certificate.</p>
          </Link>
        </section>
      </div>
    </Layout>
  );
}
